import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SAGE } from '../theme';
import useAuth from '../hooks/useAuth';
import SectionHeader from '../components/dashboard/SectionHeader';
import AddWidgetCard from '../components/dashboard/AddWidgetCard';
import AddSectionTile from '../components/dashboard/AddSectionTile';
import WidgetShell from '../components/dashboard/WidgetShell';
import CourseConversationModal from '../components/enhancement/CourseConversationModal';
import InterviewContinueCard from '../components/dashboard/widgets/InterviewContinueCard';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

/**
 * ExplorerDashboard — for users in the "figuring it out" career moment.
 *
 * Anchors today's view around what the user is *exploring*: 3 role families
 * + 1 company. Each card is one tap to the deep page. Page background is
 * light sage; cards invert to dark sage on hover for a tactile browsing feel.
 *
 * Display name resolution: when an admin is impersonating via View As, the
 * /me payload still describes the *real* admin user. We prefer the
 * viewingAs.name so the page reads as the impersonated user.
 */

// Demo content for Margot — Senior Recruiter exploring Product Marketing
// post-MBA. The picks here are deliberate: PMM as the central thesis, two
// adjacent marketing roles, and Anthropic as the aspirational company tied
// to the AI thread of her MBA.
const EXPLORE_ROLES = [
  {
    id: 'product_marketing_manager',
    title: 'Product Marketing Manager',
    bet: 'A function that rewards story, segmentation, and stakeholder partnership — three things recruiting taught you cold.',
    cta: 'See what it takes →',
    href: '/explore/roles/marketing?role=product_marketing_manager',
    primary: true,
  },
  {
    id: 'content_marketer',
    title: 'Content Marketer',
    bet: 'If positioning is the thesis, content is the proof. The lighter-weight cousin to PMM.',
    cta: 'See what it takes →',
    href: '/explore/roles/marketing?role=content_marketer',
  },
  {
    id: 'growth_marketer',
    title: 'Growth Marketer',
    bet: 'A funnel-led, quantitative path. Same audience instincts, sharper math.',
    cta: 'See what it takes →',
    href: '/explore/roles/marketing?role=growth_marketer',
  },
];

const EXPLORE_COMPANIES = [
  {
    id: 'anthropic',
    title: 'Anthropic',
    bet: 'The AI policy thread of your MBA, made operational. Marketing here is half category-defining narrative, half customer education.',
    cta: 'See inside Anthropic →',
    href: '/explore/companies/anthropic.com',
  },
];

// Margot's current Foster MBA quarter — Spring 2026.
// When the course detail page lands, swap onClick to navigate to the page;
// the per-course modal can become a button inside that page instead.
const CURRENT_COURSES = [
  {
    code: 'B ECON 501 C',
    title: 'Analysis of Global Economic Conditions',
    quarter: 'Spring 2026',
    bet: 'The frame for reading PMM-relevant macro signal — pricing, demand, regional appetite for AI tooling.',
    prompts: [
      'Country/region case study',
      'Macro forecast project',
      'Group presentation',
      "Skills you're picking up",
    ],
  },
  {
    code: 'I S 500',
    title: 'Data Management for Analytics',
    quarter: 'Spring 2026',
    bet: 'The data fluency that turns "I have a hunch" into "I ran the query." Closes a long-flagged PMM gap.',
    prompts: [
      'SQL Project',
      'Database design assignment',
      'Final analytics project',
      "Skills you're picking up",
    ],
  },
  {
    code: 'FIN 557',
    title: 'Entrepreneurial Finance',
    quarter: 'Spring 2026',
    bet: 'Cap tables, valuation, term sheets — the financial vocabulary of the AI-native startups you want to market for.',
    prompts: [
      'Startup valuation case',
      'Term sheet exercise',
      'Group cap-table project',
      "Skills you're picking up",
    ],
  },
];

const ExplorerDashboard = () => {
  const navigate = useNavigate();
  const { account, viewingAs, isAdmin } = useAuth();
  const [activeCourse, setActiveCourse] = useState(null);

  // When admin is impersonating, the /me payload still describes the real
  // user. Prefer the impersonated identity so the page reads as that user.
  const displayName =
    (viewingAs?.name && viewingAs.name.split(' ')[0]) ||
    account?.preferred_name ||
    (account?.name && account.name.split(' ')[0]) ||
    'there';

  return (
    <Box
      sx={{
        bgcolor: SAGE[100],
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 5 } }}>
        {/* Header */}
        <Box sx={{ mb: 5 }}>
          <Typography
            sx={{
              fontFamily: F,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: 1.8,
              textTransform: 'uppercase',
              color: SAGE[600],
              mb: 1,
            }}
          >
            The Explorer
          </Typography>
          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: { xs: '1.8rem', md: '2.4rem' },
              fontWeight: 500,
              color: SAGE[800],
              lineHeight: 1.15,
            }}
          >
            Welcome back, {displayName}.
          </Typography>
          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: '0.95rem',
              fontStyle: 'italic',
              color: SAGE[600],
              mt: 0.75,
            }}
          >
            Something's shifting — let's figure out what's pulling at you.
          </Typography>
        </Box>

        {/* Section: What you're exploring (roles) */}
        <Box sx={{ mb: 5 }}>
          <SectionHeader
            label="Roles you're exploring"
            sub="Three you've pinned. Each opens to what the role asks for vs. what's already in your background."
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            {EXPLORE_ROLES.map((r) => (
              <WidgetShell
                key={r.id}
                kind="Exploring · Role"
                title={r.title}
                bet={r.bet}
                footerCta={r.cta}
                onFooterClick={() => navigate(r.href)}
                primary={r.primary}
              />
            ))}
          </Box>
        </Box>

        {/* Section: Courses you're taking — capture experience as you go */}
        <Box sx={{ mb: 5 }}>
          <SectionHeader
            label="This quarter at Foster"
            sub="Click any class to capture what you're working on — a project, a case, the skills you're picking up — and we'll draft resume bullets from your story."
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            {CURRENT_COURSES.map((c) => (
              <WidgetShell
                key={c.code}
                kind={`In progress · ${c.code}`}
                title={c.title}
                bet={c.bet}
                footerCta="Capture your experience →"
                onFooterClick={() => setActiveCourse(c)}
              />
            ))}
          </Box>
        </Box>

        {/* Section: Companies you're watching */}
        <Box sx={{ mb: 5 }}>
          <SectionHeader
            label="Companies you're watching"
            sub="Pin a company you're curious about — we'll show you the inside view: who they hire, how they hire, and what working there asks of you."
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            {EXPLORE_COMPANIES.map((c) => (
              <WidgetShell
                key={c.id}
                kind="Exploring · Company"
                title={c.title}
                bet={c.bet}
                footerCta={c.cta}
                onFooterClick={() => navigate(c.href)}
              />
            ))}
            <AddWidgetCard label="Pin another company" sub="Anthropic, OpenAI, Stripe, Notion — whatever's been pulling at you." />
          </Box>
        </Box>

        {/* Section: Your initiatives — empty slot, just the AddWidget tile */}
        <Box sx={{ mb: 5 }}>
          <SectionHeader
            label="Initiatives in flight"
            sub="Bets you're placing while the destination clarifies."
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
            }}
          >
            {isAdmin && <InterviewContinueCard />}
            <AddWidgetCard label="Add an initiative" sub="A reading list, a side project, a coffee chat — the bets you want to track." />
          </Box>
        </Box>

        {/* Page-level customization hint */}
        <AddSectionTile />
      </Box>

      <CourseConversationModal
        open={!!activeCourse}
        onClose={() => setActiveCourse(null)}
        course={activeCourse}
        programLabel="Foster MBA"
      />
    </Box>
  );
};

export default ExplorerDashboard;
